import * as React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import CardActionArea from '@mui/material/CardActionArea';
import Grid from '@mui/material/Grid';
import { useNavigate } from "react-router-dom";
import { useOrderDraft } from '../Components/Orders/OrderDraftContext';

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

function ListCard({ customers, maxHeight = '75vh' }) {
    const navigate = useNavigate();
    const { setCustomer } = useOrderDraft();
    const scrollContainerRef = React.useRef(null);

    // Sort customers alphabetically by StoreName
    const sortedCustomers = React.useMemo(() => {
        return [...customers].sort((a, b) => {
            const nameA = (a.StoreName || "").trim().toLowerCase();
            const nameB = (b.StoreName || "").trim().toLowerCase();
            return nameA.localeCompare(nameB);
        });
    }, [customers]);

    // Track active letters (letters for which there are customers)
    const activeLetters = React.useMemo(() => {
        const set = new Set();
        sortedCustomers.forEach(c => {
            if (c.StoreName) {
                const firstChar = c.StoreName.trim().charAt(0).toUpperCase();
                if (/[A-Z]/.test(firstChar)) {
                    set.add(firstChar);
                }
            }
        });
        return set;
    }, [sortedCustomers]);

    // Map each active letter to the ID of its first customer element
    const firstCustomerIdsByLetter = React.useMemo(() => {
        const map = {};
        sortedCustomers.forEach(c => {
            if (c.StoreName) {
                const firstChar = c.StoreName.trim().charAt(0).toUpperCase();
                if (/[A-Z]/.test(firstChar) && !map[firstChar]) {
                    map[firstChar] = c._id;
                }
            }
        });
        return map;
    }, [sortedCustomers]);

    const handleLetterClick = (letter) => {
        const customerId = firstCustomerIdsByLetter[letter];
        if (customerId && scrollContainerRef.current) {
            const targetEl = document.getElementById(`customer-card-${customerId}`);
            if (targetEl) {
                targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    };

    const handleClick = (customer) => {
        try {
            setCustomer(customer);
            navigate(`/SelectAProduct/${customer?.customerid}/${encodeURIComponent(customer?.StoreName)}`);
        } catch (error) {
            console.error("Error in handle click", error);
        }
    };

    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                padding: 1,
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 2,
                    position: 'relative'
                }}
            >
                {/* Scrollable Customer Grid Container */}
                <Box
                    ref={scrollContainerRef}
                    sx={{
                        flexGrow: 1,
                        maxHeight: maxHeight,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        paddingRight: 2,
                        '&::-webkit-scrollbar': {
                            width: '6px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'rgba(0,0,0,0.15)',
                            borderRadius: '3px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            backgroundColor: 'rgba(0,0,0,0.3)',
                        },
                    }}
                >
                    <Grid container spacing={3} sx={{ marginBottom: "20px" }}>
                        {sortedCustomers.map((customer) => {
                            return (
                                <Grid
                                    item
                                    xs={12}
                                    sm={6}
                                    md={4}
                                    key={customer._id}
                                    id={`customer-card-${customer._id}`}
                                >
                                    <Card
                                        sx={{
                                            height: '100%',
                                            borderRadius: 2,
                                            border: '1px solid rgba(0, 0, 0, 0.08)',
                                            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                                            '&:hover': {
                                                boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                                                // transform: 'translateY(-4px)',
                                                borderColor: 'primary.light',
                                            },
                                        }}
                                    >
                                        <CardActionArea
                                            onClick={() => handleClick(customer)}
                                            sx={{
                                                height: '100%',
                                                p: 0.5,
                                            }}
                                        >
                                            <CardContent sx={{ height: '100%', p: 2.5 }}>
                                                {/* Store Name - Bold */}
                                                <Typography
                                                    variant="subtitle1"
                                                    component="div"
                                                    fontWeight="700"
                                                    sx={{ color: '#1a1a1a', mb: 0.5 }}
                                                >
                                                    {customer?.StoreName}
                                                </Typography>

                                                {/* Customer ID */}
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    display="block"
                                                    sx={{ mb: 1, fontWeight: 500 }}
                                                >
                                                    Customer ID# {customer?.customerid}
                                                </Typography>

                                                {/* Contact Name */}
                                                <Typography
                                                    variant="body2"
                                                    fontWeight="600"
                                                    color="text.primary"
                                                    sx={{ mb: 1.5 }}
                                                >
                                                    {`${customer?.firstname || ''} ${customer?.lastname || ''}`.trim() || 'No Name Listed'}
                                                </Typography>

                                                {/* Shipping Address Label */}
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: 'primary.main',
                                                        fontWeight: 'bold',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px',
                                                        display: 'block',
                                                        mb: 0.5
                                                    }}
                                                >
                                                    Shipping Address:
                                                </Typography>

                                                {/* Address - Bold */}
                                                <Typography
                                                    variant="body2"
                                                    fontWeight="500"
                                                    color="text.primary"
                                                    sx={{ lineHeight: 1.4 }}
                                                >
                                                    {[
                                                        customer?.s_address1,
                                                        customer?.s_address2,
                                                        customer?.s_city,
                                                        customer?.s_state,
                                                        customer?.s_postcode,
                                                        customer?.s_country
                                                    ].filter(Boolean).join(", ") || 'No address provided'}
                                                </Typography>
                                            </CardContent>
                                        </CardActionArea>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Box>

                {/* Vertical A-Z Quick Jump Sidebar */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '24px',
                        borderLeft: '1px solid rgba(0, 0, 0, 0.08)',
                        pl: 1,
                        userSelect: 'none',
                    }}
                >
                    {ALPHABET.map(letter => {
                        const hasMatches = activeLetters.has(letter);
                        return (
                            <Typography
                                key={letter}
                                onClick={() => hasMatches && handleLetterClick(letter)}
                                sx={{
                                    cursor: hasMatches ? 'pointer' : 'default',
                                    fontSize: '0.7rem',
                                    fontWeight: '700',
                                    color: hasMatches ? 'primary.main' : 'text.disabled',
                                    textAlign: 'center',
                                    width: '100%',
                                    py: 0.2,
                                    borderRadius: '50%',
                                    transition: 'all 0.15s ease',
                                    '&:hover': hasMatches ? {
                                        color: 'primary.contrastText',
                                        backgroundColor: 'primary.main',
                                        transform: 'scale(1.2)'
                                    } : {}
                                }}
                            >
                                {letter}
                            </Typography>
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );
}

export default ListCard;

